<script setup lang="ts">
import { computed } from 'vue'
import { useLocalePath } from '#i18n'
import AppLanguageSwitcher from '~/components/navigation/AppLanguageSwitcher.vue'
import type { FooterProps } from '~/models/layouts/footer.model'
import AppIcon from '~/components/elements/AppIcon.vue'

const {
  footerLinks = [],
  showVersion = true,
  showLanguageSwitcher = true,
} = defineProps<FooterProps>()

const localePath = useLocalePath()
const config = useRuntimeConfig()

const version = computed(() => config.public.version)

const footerClasses = 'relative px-4 py-4 sm:px-6 sm:py-5 md:px-12 md:py-6'
const footerContentClasses =
  'container mx-auto flex flex-wrap md:flex-nowrap items-center justify-center gap-x-6 gap-y-2'

const linkRowClasses =
  'grow text-md mt-0 flex flex-row flex-wrap items-center justify-center gap-2'
const linkClasses =
  'cursor-pointer whitespace-nowrap flex gap-1 items-center flex-nowrap text-basics-text-secondary hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-500'

const versionClasses = 'text-md text-basics-text-secondary break-all'

const isExternalLink = (link: string) => link.startsWith('http')

const EXTERNAL_LINK_ICON = {
  name: 'fa6-solid:arrow-up-right-from-square',
  color: 'basics-text-secondary',
  size: 'xs',
} as const
</script>

<template>
  <footer :class="footerClasses" data-testid="app-footer">
    <div :class="footerContentClasses">
      <!-- Version -->
      <div v-if="showVersion" :class="versionClasses" data-testid="app-version">
        {{ version }}
      </div>
      <!-- Links -->
      <div :class="linkRowClasses">
        <template v-for="(link, index) in footerLinks" :key="link.to">
          <NuxtLink
            :to="localePath(link.to)"
            :external="isExternalLink(link.to)"
            :rel="isExternalLink(link.to) ? 'noopener noreferrer' : null"
            :class="linkClasses"
            :aria-label="link.label"
            data-testid="footer-link"
            target="_blank"
          >
            {{ link.label
            }}<AppIcon
              v-if="isExternalLink(link.to)"
              v-bind="EXTERNAL_LINK_ICON"
              data-testid="external-link-icon"
              aria-hidden="true"
            />
          </NuxtLink>
          <span
            v-if="index < footerLinks.length - 1"
            class="text-gray-200"
            aria-hidden="true"
            >|</span
          >
        </template>
      </div>

      <!-- Language Switcher -->
      <AppLanguageSwitcher
        v-if="showLanguageSwitcher"
        force-refresh
        drop-direction="up"
        class="mt-0"
      />
    </div>
  </footer>
</template>
